import React from "react";
import { Avatar, Button, CircularProgress, IconButton, TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useSearchParams } from "react-router-dom";
import { AuthBox, AuthTitle, SectionAuth } from "../Auth/styled";
import {
  fetchPasswordLink,
  InvalidLinkError,
  passwordProblem,
  submitPasswordLink,
} from "./setPasswordApi";

type Stage = "loading" | "invalid" | "form" | "done";

const textStyle: React.CSSProperties = {
  color: "var(--g-text)",
  fontSize: 14,
  lineHeight: 1.5,
  textAlign: "center",
  margin: "0 0 24px 0",
  wordBreak: "break-word",
};

/** Opens the login page with a full reload, so the auth state starts fresh. */
const loginHref = "/";

/**
 * Public page of the one-time link a Gamixter manager sends: the user sets their own password.
 * Works logged in and logged out; the token in the URL is the only credential.
 */
export const SetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [stage, setStage] = React.useState<Stage>(token ? "loading" : "invalid");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [touched, setTouched] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetchPasswordLink(token)
      .then((res) => {
        if (cancelled) return;
        setEmail(res.email);
        setStage("form");
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof InvalidLinkError) setStage("invalid");
        else {
          setError(e?.message || "Something went wrong");
          setStage("form");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const problem = passwordProblem(password, confirm);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (problem) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitPasswordLink(token, password);
      setStage("done");
    } catch (err) {
      if (err instanceof InvalidLinkError) setStage("invalid");
      else setError((err as Error)?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const visibility = (
    <IconButton
      aria-label={showPassword ? "Hide password" : "Show password"}
      onClick={() => setShowPassword((v) => !v)}
      edge="end"
    >
      {showPassword ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
    </IconButton>
  );

  return (
    <SectionAuth>
      <AuthBox>
        <Avatar
          variant="rounded"
          sx={{
            marginBottom: "45px",
            width: 60,
            height: 60,
            boxShadow: "0px 0px 16px 0px #0000001A",
            background: "var(--g-surface)",
            borderRadius: "12px",
            "& svg path": {
              fill: "gray",
            },
          }}
        />

        {stage === "loading" && <CircularProgress size={28} />}

        {stage === "invalid" && (
          <>
            <AuthTitle>Link is invalid or expired</AuthTitle>
            <p style={textStyle}>Ask your Gamixter manager for a new link.</p>
            <Button href={loginHref} variant="outlined" fullWidth>
              Go to sign in
            </Button>
          </>
        )}

        {stage === "done" && (
          <>
            <AuthTitle>Password changed</AuthTitle>
            <p style={textStyle}>You can now sign in with your new password.</p>
            <Button href={loginHref} variant="contained" fullWidth>
              Sign in
            </Button>
          </>
        )}

        {stage === "form" && (
          <>
            <AuthTitle>Set a new password</AuthTitle>
            {email && (
              <p style={textStyle}>
                Set a new password for <b>{email}</b>
              </p>
            )}
            <form onSubmit={onSubmit} style={{ width: "100%" }} noValidate>
              <TextField
                type={showPassword ? "text" : "password"}
                placeholder="New password"
                autoComplete="new-password"
                inputProps={{ "aria-label": "New password" }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                focused={false}
                sx={{ marginBottom: "8px" }}
                InputProps={{ endAdornment: visibility }}
              />
              <TextField
                type={showPassword ? "text" : "password"}
                placeholder="Repeat password"
                autoComplete="new-password"
                inputProps={{ "aria-label": "Repeat password" }}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                fullWidth
                focused={false}
                error={touched && !!problem}
                helperText={(touched && problem) || error || " "}
                FormHelperTextProps={{
                  sx: { color: error && !(touched && problem) ? "var(--g-danger)" : undefined },
                }}
                sx={{ marginBottom: "16px" }}
              />
              <LoadingButton
                type="submit"
                variant="contained"
                fullWidth
                loading={submitting}
              >
                Save password
              </LoadingButton>
            </form>
          </>
        )}
      </AuthBox>
    </SectionAuth>
  );
};

export default SetPasswordPage;
